/**
 * src/context/ToastContext.js — Premium toast notifications
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);
let _id = 0;

const TOAST_STYLES = {
  success: { icon:'✓', bar:'bg-green-500',  bg:'bg-white', text:'text-green-700', border:'border-green-200' },
  error:   { icon:'!', bar:'bg-red-500',    bg:'bg-white', text:'text-red-700',   border:'border-red-200'   },
  warning: { icon:'⚠', bar:'bg-amber-400',  bg:'bg-white', text:'text-amber-700', border:'border-amber-200' },
  info:    { icon:'i', bar:'bg-blue-500',   bg:'bg-white', text:'text-blue-700',  border:'border-blue-200'  },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const push = useCallback((type, message, duration = 4000) => {
    const id = ++_id;
    setToasts(t => [...t.slice(-4), { id, type, message, exiting: false }]); // max 5
    setTimeout(() => {
      setToasts(t => t.map(x => x.id === id ? { ...x, exiting: true } : x));
      setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 300);
    }, duration);
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(t => t.map(x => x.id === id ? { ...x, exiting: true } : x));
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 300);
  }, []);

  const toast = {
    success: (m, d) => push('success', m, d),
    error:   (m, d) => push('error',   m, d),
    warning: (m, d) => push('warning', m, d),
    info:    (m, d) => push('info',    m, d),
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}

      {/* Toast container */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2.5 pointer-events-none"
        style={{ maxWidth: '360px', width: 'calc(100vw - 2rem)' }}>
        {toasts.map(t => {
          const s = TOAST_STYLES[t.type] || TOAST_STYLES.info;
          return (
            <div key={t.id}
              className={`
                ${s.bg} ${s.border}
                pointer-events-auto relative flex items-start gap-3 p-4
                rounded-2xl shadow-2xl shadow-black/15 border
                transition-all duration-300
                ${t.exiting
                  ? 'opacity-0 translate-x-4 scale-95'
                  : 'opacity-100 translate-x-0 scale-100 animate-slide-in'
                }
              `}
            >
              {/* Colored accent bar */}
              <div className={`${s.bar} w-1 self-stretch rounded-full flex-shrink-0`}/>

              {/* Icon */}
              <div className={`w-6 h-6 rounded-full ${s.bar} flex items-center justify-center
                text-white text-xs font-black flex-shrink-0 mt-0.5 shadow-sm`}>
                {s.icon}
              </div>

              {/* Message */}
              <p className={`text-sm ${s.text} font-semibold flex-1 leading-relaxed`}>
                {t.message}
              </p>

              {/* Dismiss */}
              <button onClick={() => dismiss(t.id)}
                className="text-gray-300 hover:text-gray-500 transition-colors text-lg leading-none flex-shrink-0 -mt-0.5">
                ×
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be inside <ToastProvider>');
  return ctx;
};
